import { useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { Bookmark, Check, Filter, Plus, RotateCcw, Search, Trash2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { API, FILTER_KEYS, FILTER_LABELS, MODELS, PRESETS, REVENUE_MODES, WINDOWS, bucketLabel, fmtCompact, getJson, useDims } from './lib';
import { Select } from './charts';

const GRANS = [{ value: '', label: 'Auto' }, { value: 'hour', label: 'Hourly' }, { value: 'day', label: 'Daily' },
  { value: 'week', label: 'Weekly' }, { value: 'month', label: 'Monthly' }];

// The analytics toolbar: period, comparison, granularity, filters and saved views. Everything writes to the URL.
export default function FilterBar({ state, update, mode = 'traffic' }) {
  const report = mode === 'report';
  const hasFilters = Object.keys(state.filters).length > 0;
  const rangeText = `${bucketLabel(state.since, 'day', true)} – ${bucketLabel(state.until, 'day', true)}`;
  return (
    <div className="an-filterbar">
      <div className="an-filter-row">
        <Select label="Date range" value={state.preset} className="an-range-select"
          onChange={v => update(v === 'custom' ? { range: 'custom', since: state.since, until: state.until } : { range: v === '30d' ? null : v, since: null, until: null })}
          options={PRESETS.map(p => ({ value: p.id, label: p.label }))} />
        {state.preset === 'custom' && (
          <span className="an-dates">
            <input type="date" aria-label="From" value={state.since} min={state.firstDay} max={state.until} onChange={e => e.target.value && update({ since: e.target.value })} />
            <span>→</span>
            <input type="date" aria-label="To" value={state.until} min={state.since} max={state.today} onChange={e => e.target.value && update({ until: e.target.value })} />
          </span>
        )}
        <span className="an-range-text"><strong>{rangeText}</strong><small>{state.days} day{state.days !== 1 ? 's' : ''}
          {state.compare && <> · vs {bucketLabel(state.prev.since, 'day')} – {bucketLabel(state.prev.until, 'day', true)}</>}</small></span>
        <AddFilter state={state} update={update} />
        <span className="an-filter-spacer" />
        {report ? <>
          <Select label="Attribution model" value={state.model} onChange={v => update({ model: v === 'last' ? null : v })} options={MODELS} />
          <Select label="Lookback window" value={state.window} onChange={v => update({ window: v === '30' ? null : v })} options={WINDOWS} />
          <Select label="Revenue mode" value={state.revenue} onChange={v => update({ revenue: v === 'net' ? null : v })} options={REVENUE_MODES} />
        </> : <>
          <label className="an-toggle"><input type="checkbox" checked={state.compare} onChange={e => update({ compare: e.target.checked ? '1' : null })} /><span />Compare</label>
          <Select label="Granularity" value={state.gran} onChange={v => update({ gran: v })} options={GRANS} />
          <SavedViews state={state} update={update} />
        </>}
      </div>
      {hasFilters && <div className="an-filter-row an-chips">
        <Filter size={14} className="an-chips-icon" />
        {Object.entries(state.filters).flatMap(([k, v]) => v.split(',').map(val => (
          <span key={`${k}:${val}`} className="an-chip"><small>{FILTER_LABELS[k]}</small>{val}
            <button type="button" aria-label={`Remove ${FILTER_LABELS[k]} ${val}`} onClick={() => {
              const rest = v.split(',').filter(x => x !== val);
              update({ [k]: rest.join(',') || null });
            }}><X size={12} /></button></span>
        )))}
        {Object.keys(state.filters).length > 0 && (
          <button type="button" className="text-btn an-reset" onClick={() => update(Object.fromEntries(FILTER_KEYS.map(k => [k, null])))}>
            <RotateCcw size={12} /> Clear filters
          </button>
        )}
      </div>}
    </div>
  );
}

function AddFilter({ state, update }) {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('source');
  const [q, setQ] = useState('');
  const dims = useDims(state.touch);
  const current = (state.filters[key] || '').split(',').filter(Boolean);
  const search = q.trim();
  const options = useMemo(() => (dims.data?.[key] || []).filter(o => !search || String(o.value).toLowerCase().includes(search.toLowerCase())), [dims.data, key, search]);
  const toggle = value => {
    if (!value) return;
    const next = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
    update({ [key]: next.join(',') || null });
  };
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className="an-add-filter"><Plus size={13} /> Add filter</button>
      </PopoverTrigger>
      <PopoverContent align="start" className="sp-popover an-filter-pop">
        <div className="an-filter-pop-tabs">
          {FILTER_KEYS.map(k => <button key={k} type="button" aria-pressed={k === key} onClick={() => { setKey(k); setQ(''); }}>{FILTER_LABELS[k]}</button>)}
        </div>
        <label className="an-filter-search"><Search size={14} /><input autoFocus value={q} onChange={e => setQ(e.target.value)}
          placeholder={`Search ${FILTER_LABELS[key].toLowerCase()}…`} aria-label={`Search ${FILTER_LABELS[key].toLowerCase()} values`} /></label>
        <div className="an-filter-options" aria-busy={dims.isFetching || dims.isLoading || undefined}>
          {dims.isError && <div className="an-state an-error" role="alert"><span>Could not load filter values.{dims.data ? ' Showing the last available values.' : ''}</span>
            <button type="button" className="btn" aria-label="Retry filter values" disabled={dims.isFetching} onClick={() => dims.refetch()}>{dims.isFetching ? 'Retrying…' : 'Try again'}</button></div>}
          {dims.isLoading ? <div className="an-state" role="status">Loading values…</div> : dims.isError && !dims.data ? null : options.length === 0 ? (
            <div className="an-state" role="status">{search ? <>No match. <button type="button" className="text-btn" onClick={() => toggle(search)}>Filter by “{search}”</button></> : 'No values.'}</div>
          ) : options.map(o => (
            <button key={o.value} type="button" aria-pressed={current.includes(String(o.value))} onClick={() => toggle(String(o.value))}>
              <span className="an-check">{current.includes(String(o.value)) && <Check size={11} />}</span>
              <span title={o.value}>{o.value}</span><small>{fmtCompact(o.count)}</small>
            </button>
          ))}
        </div>
        <p className="an-filter-hint">{key === 'page' || key === 'host' ? 'Page filters limit visits, and limit people to those who visited.' : 'Counts are contacts, all time. Pick several to combine them (OR).'}</p>
      </PopoverContent>
    </Popover>
  );
}

function SavedViews({ state, update }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [saving, setSaving] = useState(false);
  const [mutationError, setMutationError] = useState('');
  const savingRef = useRef(false);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const deletingRef = useRef(false);
  const deleteTrigger = useRef(null);
  const viewsTrigger = useRef(null);
  const views = useQuery({ queryKey: ['analytics-views'], queryFn: () => getJson(`${API}/views`), enabled: open });
  const params = Object.fromEntries([...state.params.entries()]);
  const save = async () => {
    if (!name.trim() || savingRef.current || deletingRef.current) return;
    savingRef.current = true;
    setSaving(true);
    setMutationError('');
    try {
      const r = await fetch(`${API}/views`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: name.trim(), params }) });
      if (!r.ok) throw new Error('Could not save the view. Try again.');
      setName('');
      await qc.invalidateQueries({ queryKey: ['analytics-views'] });
      toast.success('View saved');
    } catch {
      setMutationError('Could not save the view. Your name is kept so you can try again.');
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!pendingDelete || deletingRef.current || savingRef.current) return;
    deletingRef.current = true;
    setDeleting(true);
    setDeleteError('');
    try {
      const r = await fetch(`${API}/views/${encodeURIComponent(pendingDelete.id)}`, { method: 'DELETE' });
      if (!r.ok) throw new Error('Could not delete the view.');
      await qc.invalidateQueries({ queryKey: ['analytics-views'] });
      setPendingDelete(null);
      toast.success('View deleted');
    } catch {
      setDeleteError('Could not delete the view. Try again or cancel.');
    } finally {
      deletingRef.current = false;
      setDeleting(false);
    }
  };
  const apply = v => {
    const all = new Set([...state.params.keys(), ...Object.keys(v.params)]);
    update(Object.fromEntries([...all].map(k => [k, v.params[k] ?? null])));
    setOpen(false);
  };
  return (<>
    <Popover open={open} onOpenChange={next => { if (!pendingDelete && !saving) setOpen(next); }}>
      <PopoverTrigger asChild>
        <button ref={viewsTrigger} type="button" className="btn small"><Bookmark size={14} /> Views</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="sp-popover an-views-pop" onInteractOutside={e => { if (pendingDelete || saving) e.preventDefault(); }}>
        <span className="sp-eyebrow">Saved views</span>
        <div className="an-views-list" aria-busy={views.isFetching || views.isLoading || undefined}>
          {views.isError && <div className="an-state an-error" role="alert"><span>Could not load saved views.{views.data?.length ? ' Showing the last saved list.' : ''}</span>
            <button type="button" className="btn" aria-label="Retry saved views" disabled={views.isFetching} onClick={() => views.refetch()}>{views.isFetching ? 'Retrying…' : 'Try again'}</button></div>}
          {views.isLoading ? <div className="an-state" role="status">Loading views…</div> : !views.isError && (views.data || []).length === 0 ? <div className="an-state" role="status">No saved views yet.</div> :
            (views.data || []).map(v => (
              <div key={v.id} className="an-view-row">
                <button type="button" disabled={saving || deleting} onClick={() => apply(v)}><strong>{v.name}</strong><small>{v.params.tab || 'overview'} · {v.params.range || (v.params.since ? 'custom' : '30d')}</small></button>
                <button type="button" className="an-view-delete" disabled={saving || deleting} aria-label={`Delete ${v.name}`}
                  onClick={e => { deleteTrigger.current = e.currentTarget; setDeleteError(''); setPendingDelete(v); }}><Trash2 size={13} /></button>
              </div>
            ))}
        </div>
        {mutationError && <p className="an-error" role="alert">{mutationError}</p>}
        <form className="an-view-save" aria-busy={saving || undefined} onSubmit={e => { e.preventDefault(); save(); }}>
          <input value={name} disabled={saving || deleting} onChange={e => setName(e.target.value)} placeholder="Name this view…" maxLength={80} aria-label="View name" />
          <button type="submit" className="btn primary small" disabled={!name.trim() || saving || deleting}>{saving ? 'Saving…' : 'Save'}</button>
        </form>
        <p className="an-filter-hint">Saves the tab, period, filters and chart settings for everyone in the workspace.</p>
      </PopoverContent>
    </Popover>
    <AlertDialog.Root open={!!pendingDelete} onOpenChange={next => { if (!next && !deletingRef.current) setPendingDelete(null); }}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="an-confirm-overlay" />
        <AlertDialog.Content className="an-confirm" aria-busy={deleting || undefined}
          onEscapeKeyDown={e => { if (deletingRef.current) e.preventDefault(); }}
          onCloseAutoFocus={e => { e.preventDefault(); (deleteTrigger.current?.isConnected ? deleteTrigger.current : viewsTrigger.current)?.focus(); }}>
          <AlertDialog.Title>Delete “{pendingDelete?.name}”?</AlertDialog.Title>
          <AlertDialog.Description>This removes the saved view for everyone in the workspace. It cannot be undone; your analytics data stays unchanged.</AlertDialog.Description>
          {deleteError && <p className="an-error" role="alert">{deleteError}</p>}
          <div className="an-confirm-actions">
            <AlertDialog.Cancel asChild><button type="button" className="btn" disabled={deleting}>Cancel</button></AlertDialog.Cancel>
            <button type="button" className="btn primary" disabled={deleting} onClick={remove}>{deleting ? 'Deleting…' : 'Delete view'}</button>
          </div>
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  </>);
}
